import React, { useState } from "react";
import axios from 'axios';
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Font,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  Alignment,
  RemoveFormat,
  Link,
  List,
  Indent,
  HorizontalLine,
  IndentBlock,
  Table,
  TableLayout,
  TableCellProperties,
  TableProperties,
  TableColumnResize,
  TableToolbar,
  Emoji,
  Mention,
  Image,
  ImageResize,
  ImageResizeButtons,
  ImageResizeEditing,
  ImageResizeHandles,
  ImageStyle,
  ImageToolbar,
  ImageInsert,
  ImageUpload,
  MediaEmbed,
  LinkImage,
  SourceEditing,
  GeneralHtmlSupport,
} from "ckeditor5";
import { API_URL, IMAGE_SOURCE_PATH } from '../../../setup/config/env';
import { Box, Paper, TextField, Button, Backdrop, Typography } from '@mui/material';
function MyUploadAdapterPlugin(editor, uploadType) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
    return new MyUploadAdapter(loader, uploadType);
  };
}

class MyUploadAdapter {
  constructor(loader, uploadType) {
    this.loader = loader;
    this.uploadType = uploadType || 'mediaGallery';
  }

  upload() {
    return this.loader.file.then((file) => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', this.uploadType);
        formData.append('name', file.name.replace(/\.[^/.]+$/, ''));

        const token = localStorage.getItem('token');
        const headers = { 'Content-Type': 'multipart/form-data' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        axios.post(`${API_URL}/upload`, formData, { headers })
          .then((response) => {
            const data = response.data;
            if (data.success && (data.file || data.url)) {
              const fileUrl = data.url || data.file;
              // If it's already a full URL (e.g. from S3), use as-is
              if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
                resolve({ default: fileUrl });
              } else {
                // Build full URL from IMAGE_SOURCE_PATH
                const base = (IMAGE_SOURCE_PATH || API_URL || '').replace(/\/$/, '');
                resolve({ default: `${base}/${fileUrl.replace(/^\//, '')}` });
              }
            } else {
              reject(data.message || 'Upload failed');
            }
          })
          .catch((error) => reject(error.message || 'Upload error'));
      });
    });
  }

  abort() {
    // no-op; axios cancellation can be added if needed
  }
}

/**
 * Call this BEFORE saving content to the database.
 * Converts absolute CKEditor image URLs back to relative paths,
 * matching the same format used by normal image uploads (e.g. \datumart\mediaGallery\image.jpg).
 *
 * Example:
 *   src="http://localhost:8000/images/datumart/mediaGallery/img.jpg"
 *   → src="/datumart/mediaGallery/img.jpg"
 */
export function stripImageHostFromContent(content) {
  if (!content || !IMAGE_SOURCE_PATH) return content;
  // Normalise trailing slash
  const base = IMAGE_SOURCE_PATH.replace(/\/$/, '');
  // Replace every occurrence of the absolute host prefix with an empty string,
  // leaving just the root-relative path (e.g. /datumart/...)
  return content.split(`src="${base}`).join('src="');
}

/**
 * Call this AFTER loading content FROM the database, before putting it in the editor.
 * Restores relative paths back to absolute URLs so CKEditor can display the images.
 *
 * Example:
 *   src="/datumart/mediaGallery/img.jpg"
 *   → src="http://localhost:8000/images/datumart/mediaGallery/img.jpg"
 *
 * Only paths that start with "/" (root-relative) and are NOT already http/https are expanded.
 */
export function restoreImageHostInContent(content) {
  if (!content || !IMAGE_SOURCE_PATH) return content;
  const base = IMAGE_SOURCE_PATH.replace(/\/$/, '');
  // Match src attributes with root-relative paths (not already absolute)
  return content.replace(/src="(\/[^"]+)"/g, (match, path) => {
    if (path.startsWith('http://') || path.startsWith('https://')) return match;
    return `src="${base}${path}"`;
  });
}



/**
 * Resolves a single image URL (e.g. from a thumbnail field) to a full absolute URL.
 * Use this when rendering <img src={url}> for any image stored as a relative path.
 *
 * - Absolute URLs (http/https) pass through unchanged (handles S3 URLs too).
 * - Relative paths (e.g. /datumart/mediaGallery/img.jpg) are prefixed with IMAGE_SOURCE_PATH.
 */
export function resolveImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = (IMAGE_SOURCE_PATH || '').replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}

const RichTextEditor = ({ value, onChange, editorRef, setEventData, uploadType }) => {
  const [showCTAModal, setShowCTAModal] = useState(false);
  const [ctaText, setCtaText] = useState('');
  const [ctaLink, setCtaLink] = useState('');
  const [ctaBgColor, setCtaBgColor] = useState('#ED6C02');
  const [ctaTextColor, setCtaTextColor] = useState('#ffffff');

  return (
    <>
      <Box sx={{
        '& .ck-content': {
          overflowWrap: 'anywhere',
          wordBreak: 'normal',
          minHeight: '300px',
          '& h1, & h2, & h3, & h4, & h5, & h6': { fontWeight: 700, mt: 2, mb: 1, color: '#1F2937' },
          '& h1': { fontSize: '2em' },
          '& h2': { fontSize: '1.5em' },
          '& h3': { fontSize: '1.17em' },
          '& h4': { fontSize: '1em' },
          '& h5': { fontSize: '0.83em' },
          '& h6': { fontSize: '0.67em' },
          '& p': { mb: 1.5, mt: 0 },
          '& strong': { fontWeight: 'bold' },
          '& em': { fontStyle: 'italic' },
          '& u': { textDecoration: 'underline' },
          '& s': { textDecoration: 'line-through' },
          '& ol': { paddingLeft: '2em', listStyleType: 'decimal' },
          '& ul': { paddingLeft: '2em', listStyleType: 'disc' }
        }
      }}>
        <CKEditor
          editor={ClassicEditor}
          data={value}
          config={{
            licenseKey: "GPL",
            plugins: [
              Essentials,
              Paragraph,
              Font,
              Heading,
              Bold,
              Italic,
              Underline,
              Strikethrough,
              Subscript,
              Superscript,
              Alignment,
              RemoveFormat,
              Link,
              List,
              Indent,
              HorizontalLine,
              IndentBlock,
              Table,
              TableLayout,
              TableCellProperties,
              TableProperties,
              TableColumnResize,
              TableToolbar,
              Emoji,
              Mention,
              Image,
              ImageUpload,
              ImageToolbar,
              ImageStyle,
              ImageResize,
              ImageResizeButtons,
              ImageResizeEditing,
              ImageResizeHandles,
              ImageInsert,
              MediaEmbed,
              LinkImage,
              SourceEditing,
              GeneralHtmlSupport
            ],
            toolbar: {
              items: [
                "undo",
                "redo",
                "|",
                "fontSize",
                "fontFamily",
                "fontColor",
                "fontBackgroundColor",
                "|",
                "heading",
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "subscript",
                "superscript",
                "|",
                "alignment",
                "bulletedList",
                "numberedList",
                "removeFormat",
                "|",
                "emoji",
                "insertImage",
                "mediaEmbed",
                "link",
                "horizontalLine",
                "outdent",
                "indent",
                "insertTable",
                "tableType",
                "sourceEditing",
              ],
              shouldNotGroupWhenFull: true,
            },
            htmlSupport: {
              allow: [
                {
                  name: /.*/,
                  attributes: true,
                  classes: true,
                  styles: true
                }
              ]
            },
            fontSize: {
              options: [11, 13, "default", 17, 19, 21],
              supportAllValues: true,
              useClasses: false,
            },
            table: {
              contentToolbar: [
                "tableColumn",
                "tableRow",
                "mergeTableCells",
                "tableProperties",
                "tableCellProperties",
                "toggleTableCaption",
              ],
            },
            emoji: {
              dropdownLimit: 5,
            },
            image: {
              resizeUnit: "%",
              resizeOptions: [
                { name: "resizeImage:original", label: "Original", value: null },
                { name: "resizeImage:custom", label: "Custom", value: "custom" },
                { name: 'resizeImage:50', value: '50', label: '50%' },
                { name: 'resizeImage:75', value: '75', label: '75%' },
                { name: 'resizeImage:100', value: '100', label: '100%' }
              ],
              toolbar: [
                "resizeImage",
                "imageStyle:inline",
                "imageStyle:block",
                "imageStyle:side",
              ],
              insert: {
                integrations: ["upload"],
              },
            },
            mediaEmbed: {
              previewsInData: true,
            },
          }}
          onReady={(editor) => {
            if (editorRef) editorRef.current = editor;
            MyUploadAdapterPlugin(editor, uploadType);

            const insertCTA = document.createElement("button");
            insertCTA.type = "button";
            insertCTA.className = "custom-cta-button";
            insertCTA.title = "Insert CTA Button";
            insertCTA.innerHTML = "🔲";

            insertCTA.onclick = () => setShowCTAModal(true);

            setTimeout(() => {
              const toolbar = editor.ui.view.toolbar.element;
              if (toolbar) {
                const lastGroup = toolbar.querySelector(".ck-toolbar__items");
                if (lastGroup) {
                  lastGroup.appendChild(insertCTA);
                }
              }
            }, 100);
          }}
          onChange={(event, editor) => {
            const data = editor.getData();
            onChange(data);
          }}
        />
      </Box>

      {showCTAModal && (
        <>
          <Backdrop open={true} sx={{ zIndex: 1200 }} onClick={() => setShowCTAModal(false)} />
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1300,
            }}
          >
            <Paper sx={{ p: 3, width: 400 }}>
              <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
                Insert CTA Button
              </Typography>
              <TextField
                fullWidth
                label="Button Text"
                variant="outlined"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Button Link"
                variant="outlined"
                value={ctaLink}
                onChange={(e) => setCtaLink(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="body2">Background Color</Typography>
                  <input
                    type="color"
                    value={ctaBgColor}
                    onChange={(e) => setCtaBgColor(e.target.value)}
                  />
                </Box>
                <Box>
                  <Typography variant="body2">Text Color</Typography>
                  <input
                    type="color"
                    value={ctaTextColor}
                    onChange={(e) => setCtaTextColor(e.target.value)}
                  />
                </Box>
              </Box>
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography variant="body2" gutterBottom>Button Preview:</Typography>
                <Box
                  sx={{
                    display: 'inline-block',
                    backgroundColor: ctaBgColor,
                    color: ctaTextColor,
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    fontWeight: 'bold',
                  }}
                >
                  {ctaText || 'CTA Button'}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={() => setShowCTAModal(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (ctaText && ctaLink && editorRef.current) {
                      const buttonHTML = `<a href="${ctaLink}" style="background-color:${ctaBgColor}; color:${ctaTextColor}; padding:5px 15px; border-radius:4px; text-decoration:none; font-weight:bold; display:inline-block;">${ctaText}</a>`;
                      const editor = editorRef.current;
                      editor.model.change(() => {
                        editor.model.insertContent(
                          editor.data.toModel(editor.data.processor.toView(buttonHTML))
                        );
                      });
                    }
                    setShowCTAModal(false);
                    setCtaText('');
                    setCtaLink('');
                    setCtaBgColor('#ED6C02');
                    setCtaTextColor('#ffffff');
                  }}
                >
                  Insert
                </Button>
              </Box>
            </Paper>
          </Box>
        </>
      )}
    </>
  );
};

export default RichTextEditor;
